/**
 * Servicio de API - Módulo de Control de Cuotas
 * 
 * Centraliza todas las llamadas al backend del módulo de cuotas
 */

import api from './api';

const BASE_URL = '/cuotas';

const cuotasApi = {
  // ========================
  // CONFIGURACIÓN
  // ========================
  
  /**
   * Obtener configuración actual
   */
  getConfiguracion: () => 
    api.get(`${BASE_URL}/configuracion`),
  
  /**
   * Actualizar configuración
   */
  updateConfiguracion: (data) => 
    api.put(`${BASE_URL}/configuracion`, data),
  
  /**
   * Obtener años disponibles con estadísticas
   */
  getAños: () => 
    api.get(`${BASE_URL}/configuracion/anos`),
  
  /**
   * Obtener preview antes de crear nuevo año
   */
  getPreviewNuevoAño: (año) => 
    api.get(`${BASE_URL}/configuracion/resumen-nuevo-ano/${año}`),
  
  /**
   * Crear nuevo año
   */
  crearNuevoAño: (data) => 
    api.post(`${BASE_URL}/configuracion/nuevo-ano`, data),

  // ========================
  // TIPOS DE CUOTA
  // ========================
  
  /**
   * Listar tipos de cuota
   * @param {Object} params - { año, esMensual, activa }
   */
  getTiposCuota: (params = {}) => 
    api.get(`${BASE_URL}/tipos-cuota`, { params }),
  
  /**
   * Obtener tipo de cuota específico
   */
  getTipoCuota: (id) => 
    api.get(`${BASE_URL}/tipos-cuota/${id}`),
  
  /**
   * Crear tipo de cuota no mensual
   */
  crearTipoCuota: (data) => 
    api.post(`${BASE_URL}/tipos-cuota`, data),
  
  /**
   * Actualizar tipo de cuota
   */
  updateTipoCuota: (id, data) => 
    api.put(`${BASE_URL}/tipos-cuota/${id}`, data),
  
  /**
   * Actualizar monto con recálculo
   */
  actualizarMontoCuota: (id, nuevoMonto) => 
    api.put(`${BASE_URL}/tipos-cuota/${id}/monto`, { nuevoMonto }),
  
  /**
   * Eliminar tipo de cuota no mensual
   */
  eliminarTipoCuota: (id) => 
    api.delete(`${BASE_URL}/tipos-cuota/${id}`),

  // ========================
  // BOMBEROS EN CUOTAS
  // ========================
  
  /**
   * Listar bomberos con estado de cuotas
   * @param {Object} params - { año, page, limit, estado, tipoBomberoId, busqueda }
   */
  getBomberos: (params = {}) => 
    api.get(`${BASE_URL}/bomberos`, { params }),
  
  /**
   * Obtener detalle de un bombero
   */
  getBomberoDetalle: (id, params = {}) => 
    api.get(`${BASE_URL}/bomberos/${id}`, { params }),
  
  /**
   * Obtener historial de cuotas de un bombero
   */
  getBomberoHistorial: (id, params = {}) => 
    api.get(`${BASE_URL}/bomberos/${id}/historial`, { params }),

  /**
   * Eliminar bombero del módulo de cuotas (soft delete)
   * NO afecta al bombero en la BD principal
   */
  eliminarBomberoDeCuotas: (id) => 
    api.delete(`${BASE_URL}/bomberos/${id}`),

  /**
   * Ajustar abono a favor de un bombero (solo admin)
   */
  ajustarAbono: (id, data) =>
    api.put(`${BASE_URL}/bomberos/${id}/abono`, data),

  // ========================
  // PAGOS
  // ========================
  
  /**
   * Listar pagos
   * @param {Object} params - { año, bomberoId, tipoCuotaId, metodoPago, fechaDesde, fechaHasta, page, limit }
   */
  getPagos: (params = {}) => 
    api.get(`${BASE_URL}/pagos`, { params }),
  
  /**
   * Registrar pago individual
   */
  registrarPago: (data) => 
    api.post(`${BASE_URL}/pagos`, data),
  
  /**
   * Registrar múltiples pagos (mismo bombero)
   */
  registrarPagoMultiple: (data) => 
    api.post(`${BASE_URL}/pagos/multiple`, data),
  
  /**
   * Anular pago
   */
  anularPago: (id, motivo) => 
    api.delete(`${BASE_URL}/pagos/${id}`, { data: { motivo } }),

  // ========================
  // EXENCIONES Y DESCUENTOS
  // ========================
  
  /**
   * Listar exenciones
   * @param {Object} params - { año, tipoExencion, estado, tipoBomberoId }
   */
  getExenciones: (params = {}) => 
    api.get(`${BASE_URL}/exenciones`, { params }),
  
  /**
   * Obtener exención específica
   */
  getExencion: (id) => 
    api.get(`${BASE_URL}/exenciones/${id}`),
  
  /**
   * Crear exención/descuento
   */
  crearExencion: (data) => 
    api.post(`${BASE_URL}/exenciones`, data),
  
  /**
   * Actualizar exención
   */
  updateExencion: (id, data) => 
    api.put(`${BASE_URL}/exenciones/${id}`, data),
  
  /**
   * Finalizar exención
   */
  finalizarExencion: (id, motivo) => 
    api.put(`${BASE_URL}/exenciones/${id}/finalizar`, { motivo }),
  
  /**
   * Eliminar exención (solo sin pagos asociados)
   */
  eliminarExencion: (id) => 
    api.delete(`${BASE_URL}/exenciones/${id}`),

  // ========================
  // ESTADÍSTICAS
  // ========================
  
  /**
   * Obtener resumen general de cuotas
   */
  getEstadisticas: (año) => 
    api.get(`${BASE_URL}/estadisticas/resumen`, { params: { año } }),
  
  /**
   * Estadísticas por tipo de bombero
   */
  getEstadisticasPorTipo: (año) => 
    api.get(`${BASE_URL}/estadisticas/por-tipo-bombero`, { params: { año } }),
  
  /**
   * Recaudación por período
   */
  getRecaudacion: (año, agrupacion = 'mes') => 
    api.get(`${BASE_URL}/estadisticas/recaudacion`, { params: { año, agrupacion } }),
  
  /**
   * Listar morosos
   */
  getMorosos: (año) => 
    api.get(`${BASE_URL}/estadisticas/morosos`, { params: { año } }),

  // ========================
  // SINCRONIZACIÓN
  // ========================
  
  /**
   * Obtener estado de sincronización
   */
  getSyncStatus: () => 
    api.get(`${BASE_URL}/sincronizacion/estado`),
  
  /**
   * Obtener tipos de bombero disponibles
   */
  getTiposBombero: () => 
    api.get(`${BASE_URL}/sincronizacion/tipos-bombero`),
  
  /**
   * Sincronizar bomberos
   */
  sincronizarBomberos: () => 
    api.post(`${BASE_URL}/sincronizacion/bomberos`),
  
  /**
   * Sincronizar tipos de bombero
   */
  sincronizarTipos: () => 
    api.post(`${BASE_URL}/sincronizacion/tipos`),
  
  /**
   * Sincronización completa
   */
  sincronizarTodo: () => 
    api.post(`${BASE_URL}/sincronizacion/completa`),

  // ========================
  // EXPORTACIÓN
  // ========================
  
  /**
   * Exportar cuotas a Excel
   * @param {number} año - Año a exportar
   * @returns {Promise} - Blob del archivo Excel
   */
  exportarExcel: (año) => 
    api.get(`${BASE_URL}/exportar/${año}`, { responseType: 'blob' })
};

export default cuotasApi;
